import type { MantineColorsTuple } from "@mantine/core";
import { useMantineTheme } from "@mantine/core";
import { useCommonColors } from "octalog-base";
import React from "react";
import { Cell, Label, Legend, Pie, PieChart, ResponsiveContainer } from "recharts";
import type { ViewBox } from "recharts/types/util/types";

import type { Translatable } from "../../common/Dictionary";

export type HolzPieChartDatum = Record<string, unknown>;

export interface HolzPieChartProps<T extends HolzPieChartDatum> {
  data: T[];
  dataNameKey: keyof T;
  dataValueKey: string;
  dataNameTranslator?: (name: string) => string;
  headline: string;
  subline: string;
  color: MantineColorsTuple;
}

export function HolzPieChart<T extends HolzPieChartDatum>({
  data,
  dataNameKey,
  dataValueKey,
  dataNameTranslator,
  headline,
  subline,
  color,
}: HolzPieChartProps<T>): React.JSX.Element {
  const theme = useMantineTheme();
  const commonColors = useCommonColors();

  // Transform data with translated names for the chart
  const transformedData = React.useMemo<T[]>(() => {
    if (!dataNameTranslator) return data;

    return data.map((item) => {
      const originalName = item[dataNameKey];

      if (typeof originalName !== "string") {
        return item;
      }

      return {
        ...item,
        [dataNameKey]: dataNameTranslator(originalName),
      } as T;
    });
  }, [data, dataNameKey, dataNameTranslator]);

  const chartData = transformedData as HolzPieChartDatum[];

  function getColor<T>(item: T, dataNameKey: keyof T, index = 0): string {
    return item[dataNameKey] !== undefined && item[dataNameKey] !== null
      ? (color[9 - (index % 6)] ?? theme.primaryColor)
      : theme.primaryColor;
  }

  return (
    <ResponsiveContainer>
      <PieChart>
        <Pie
          data={chartData}
          dataKey={dataValueKey}
          innerRadius="40%"
          label
          labelLine={false}
          outerRadius="50%"
          paddingAngle={7}
        >
          <Label
            content={(props: { viewBox?: ViewBox | undefined }) => (
              <HolzPieChartLabel
                headline={headline}
                subline={subline}
                textColor={commonColors.text}
                viewBox={props.viewBox}
              />
            )}
            style={{ fill: commonColors.text }}
          />
          {transformedData.map((item, index) => (
            <Cell fill={getColor<T>(item, dataNameKey, index)} key={item[dataNameKey] as Translatable} stroke="none" />
          ))}
        </Pie>
        <Legend align="center" layout="horizontal" verticalAlign="bottom" />
      </PieChart>
    </ResponsiveContainer>
  );
}

interface HolzPieChartLabelProps {
  viewBox?: ViewBox;
  headline: string;
  subline: string;
  textColor: string;
}

function HolzPieChartLabel({ viewBox, headline, subline, textColor }: HolzPieChartLabelProps) {
  // Handle both PolarViewBox (which has cx, cy) and CartesianViewBox (which has x, y, width, height)
  const cx =
    viewBox && "cx" in viewBox
      ? viewBox.cx
      : viewBox && "x" in viewBox && viewBox.x != null && viewBox.width != null
        ? viewBox.x + viewBox.width / 2
        : 0;
  const cy =
    viewBox && "cy" in viewBox
      ? viewBox.cy
      : viewBox && "y" in viewBox && viewBox.y != null && viewBox.height != null
        ? viewBox.y + viewBox.height / 2
        : 0;

  return (
    <text dy="-6" fill={textColor} textAnchor="middle" x={cx} y={cy}>
      <tspan>{headline}</tspan>
      <tspan dy="1.2em" x={cx}>
        {subline}
      </tspan>
    </text>
  );
}
